import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {AbstractHttpService} from './abstract-http-service';
import {OfferRuleModel} from '../model/offer-rule.model';
import {EligibleOfferSetModel} from '../model/eligible-offer-set.model';
import {BulkOfferRuleRequest, BulkOfferRuleResponse} from '../model/bulk-offer-rule.model';

@Injectable({
  providedIn: 'root'
})
export class OfferRuleService extends AbstractHttpService {
  private options = {
    headers: this.contentTypeJsonHeaders,
    withCredentials: true
  };

  private patchOptions = {
    headers: this.contentTypeJsonHeadersForPatch,
    withCredentials: true
  };

  private readOptions = {
    headers: this.acceptJsonHeaders,
    withCredentials: true
  };

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Gets offer rules by account id
   * @param accountId
   */
  getOfferRules(accountId: number): Observable<OfferRuleModel[]> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offer-rule?accountId=${accountId}`;
    const response = this.httpClient.get<OfferRuleModel[]>(requestUrl, this.readOptions).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Atomically persists the current account rule change set.
   */
  bulkSaveOfferRules(request: BulkOfferRuleRequest): Observable<BulkOfferRuleResponse> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offer-rule/bulk`;
    const response = this.httpClient.post<BulkOfferRuleResponse>(requestUrl, request, this.options).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Creates a new offer rule
   * @param payload
   */
  createOfferRule(payload: OfferRuleModel): Observable<OfferRuleModel> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offer-rule`;
    const response = this.httpClient.post<OfferRuleModel>(requestUrl, payload, this.options).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Updates an existing offer rule
   * @param id
   * @param payload
   */
  updateOfferRule(id: number, payload: OfferRuleModel): Observable<OfferRuleModel> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offer-rule/${id}`;
    const response = this.httpClient.patch<OfferRuleModel>(requestUrl, payload, this.patchOptions).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Deletes an offer rule by id
   * @param id
   */
  deleteOfferRule(id: number): Observable<void> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offer-rule/${id}`;
    const response = this.httpClient.delete<void>(requestUrl, this.options).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Gets eligible offer sets for an account
   * @param accountId
   */
  getEligibleOfferSets(accountId: number): Observable<EligibleOfferSetModel[]> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offer-rule/eligible-offer-sets?accountId=${accountId}`;
    const response = this.httpClient.get<EligibleOfferSetModel[]>(requestUrl, this.readOptions).pipe(
      catchError(this.handleError)
    );

    return response;
  }
}
